import { X, Loader2, Check, Sparkles } from "lucide-react";

const GenerationHistorySidebar = ({
  showGenerationHistory,
  setShowGenerationHistory,
  generatedImages = [],
  selectedGenerationImage,
  setSelectedGenerationImage,
  setCharacterImage,
  loading = false,
}) => {
  const handleImageSelect = (imageData) => {
    setSelectedGenerationImage(imageData);
    setCharacterImage(imageData.originalUrl);
    setShowGenerationHistory(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTimeMs = now - date;
    const diffSeconds = Math.max(0, Math.floor(diffTimeMs / 1000));
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSeconds < 60) {
      if (diffSeconds <= 1) return "1 second ago";
      return `${diffSeconds} seconds ago`;
    }
    if (diffMinutes < 60) {
      if (diffMinutes === 1) return "1 min ago";
      return `${diffMinutes} mins ago`;
    }
    if (diffHours < 24) {
      if (diffHours === 1) return "1 hour ago";
      return `${diffHours} hours ago`;
    }
    if (diffDays < 7) {
      if (diffDays === 1) return "1 day ago";
      return `${diffDays} days ago`;
    }
    if (diffDays < 30) {
      const diffWeeks = Math.floor(diffDays / 7);
      if (diffWeeks === 1) return "1 week ago";
      return `${diffWeeks} weeks ago`;
    }
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths === 1) return "1 month ago";
    if (diffMonths < 12) return `${diffMonths} months ago`;

    const diffYears = Math.floor(diffDays / 365);
    if (diffYears === 1) return "1 year ago";
    return `${diffYears} years ago`;
  };

  return (
    <>
      {/* Backdrop overlay for clicking outside to close */}
      {showGenerationHistory && (
        <div
          className="fixed inset-0 z-40 bg-black/20 dark:bg-black/40 transition-opacity"
          onClick={() => setShowGenerationHistory(false)}
        />
      )}

      <div
        className={`fixed inset-y-0 right-0 box-border pt-14 w-full max-w-[500px] overflow-x-hidden bg-white dark:bg-[#0a0a0a]
          border-l border-gray-200 dark:border-white/10 shadow-xl transform
          transition-transform duration-300 ease-in-out z-50 will-change-transform
          ${showGenerationHistory ? "translate-x-0" : "translate-x-full"}`}
      >
      <div className="flex flex-col h-full">
        {/* HEADER */}
        <div className="flex items-start justify-between gap-3 p-4 sm:p-6 border-b border-gray-200 dark:border-white/10">
          <div className="min-w-0">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Generation History
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Your previously generated character scenes
            </p>
          </div>

          <button
            type="button"
            aria-label="Close generation history"
            onClick={() => setShowGenerationHistory(false)}
            className="shrink-0 p-2 rounded-lg text-gray-500 hover:text-gray-700
            dark:text-gray-400 dark:hover:text-gray-300 hover:bg-gray-100
            dark:hover:bg-gray-800"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* GENERATED IMAGES LIST */}
        <div className="flex-1 overflow-y-auto min-h-0 overscroll-contain scroll-smooth">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-64">
              <Loader2 className="w-8 h-8 text-orange-500 animate-spin mb-3" />
              <p className="text-gray-500 dark:text-gray-400">Loading history...</p>
            </div>
          ) : (
            <div className="p-4 sm:p-6 space-y-3">
              {generatedImages.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 dark:text-gray-400">
                    No generated images found.
                  </p>
                </div>
              ) : (
                generatedImages.map((image) => {
                  const isSelected =
                    selectedGenerationImage?._id === image._id ||
                    selectedGenerationImage?.originalUrl === image.originalUrl;

                  return (
                    <button
                      key={image._id}
                      onClick={() => handleImageSelect(image)}
                      className={`w-full min-w-0 text-left rounded-lg border overflow-hidden flex transition-all ${
                        isSelected
                          ? "border-orange-500 ring-2 ring-orange-500/50"
                          : "border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/20 hover:shadow-md"
                      }`}
                    >
                      <div className="relative w-24 h-32 flex-shrink-0 bg-gray-100 dark:bg-gray-800">
                        {!image.originalUrl || image.status === "processing" || image.status === "editing" || image.status === "pending" ? (
                          <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 animate-pulse flex flex-col items-center justify-center z-10">
                            <div className="relative">
                              <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
                              <Sparkles className="w-3 h-3 text-orange-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                            </div>
                            <p className="mt-2 px-1 text-gray-500 dark:text-gray-400 font-medium text-[10px] animate-pulse text-center leading-tight">Generating<br/>Scene...</p>
                          </div>
                        ) : (
                          <img
                            src={image.originalUrl}
                            alt="Generated character"
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      <div className="min-w-0 p-3 flex-1 bg-gray-50 dark:bg-white/5 flex justify-between items-start gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium mb-1 text-gray-900 dark:text-white">
                            Generated Character
                          </p>
                          <p className="text-xs mb-2 text-gray-600 dark:text-gray-400">
                            {image.source === "text-to-image"
                              ? "Text to Image"
                              : "Custom Generation"}
                          </p>
                          <span className="text-xs text-gray-500 dark:text-gray-500">
                            Generated {formatDate(image.createdAt)}
                          </span>
                        </div>
                        {isSelected && (
                          <div className="w-5 h-5 rounded-full bg-orange-500 text-white flex items-center justify-center flex-shrink-0">
                            <Check className="h-3 w-3" />
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default GenerationHistorySidebar;
